import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const SWATCHES = [
    { name: 'Kumkum', hex: '#B32B1C', meaning: 'the vow' },
    { name: 'Haldi', hex: '#E0A42A', meaning: 'the blessing' },
    { name: 'Jasmine', hex: '#F3E8D3', meaning: 'the fragrance' },
    { name: 'Mehndi', hex: '#5A6B2F', meaning: 'the promise' },
    { name: 'Kanchi Gold', hex: '#C9933A', meaning: 'the heritage' },
];

export const TeluguPalette = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end start'],
    });
    const leftY = useTransform(scrollYProgress, [0, 1], ['6%', '-10%']);
    const rightY = useTransform(scrollYProgress, [0, 1], ['-10%', '6%']);

    return (
        <section
            ref={ref}
            data-testid="palette-section"
            className="relative py-24 md:py-32 overflow-hidden"
            style={{ backgroundColor: '#1A0D08' }}
        >
            {/* warm radial glow */}
            <div
                className="absolute inset-0 opacity-70 pointer-events-none"
                style={{
                    background:
                        'radial-gradient(ellipse at 25% 30%, rgba(227,138,54,0.18), transparent 55%), radial-gradient(ellipse at 85% 70%, rgba(179,43,28,0.20), transparent 60%)',
                }}
            />
            <div className="film-grain absolute inset-0 pointer-events-none" />

            <div className="relative z-10 px-6 md:px-12 lg:px-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                {/* Left: editorial quote */}
                <div className="lg:col-span-6">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-120px' }}
                        transition={{ duration: 0.9 }}
                        className="text-[11px] uppercase tracking-[0.45em] mb-7"
                        style={{ color: '#E8B86A' }}
                    >
                        The Palette · A Telugu Wedding
                    </motion.p>

                    <motion.h2
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-120px' }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="font-serif font-light tracking-tighter leading-[1.02] text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem]"
                        style={{ color: '#F7EBD5' }}
                    >
                        A Telugu wedding carries{' '}
                        <span
                            className="italic"
                            style={{ color: '#E8B86A' }}
                        >
                            colours
                        </span>{' '}
                        that represent{' '}
                        <span
                            className="italic"
                            style={{ color: '#E27D5A' }}
                        >
                            this entire universe.
                        </span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.9, delay: 0.3 }}
                        className="mt-9 max-w-lg text-base md:text-lg font-light leading-relaxed"
                        style={{ color: '#D7C5A3' }}
                    >
                        Kumkum red for the vow. Haldi gold for the blessing. Jasmine for
                        the fragrance that stays long after the lights go down. We frame
                        them like the paintings they already are.
                    </motion.p>

                    {/* Color swatches */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="mt-12 grid grid-cols-5 gap-3 max-w-xl"
                    >
                        {SWATCHES.map((s, i) => (
                            <motion.div
                                key={s.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.6 + i * 0.08 }}
                                data-testid={`palette-swatch-${s.name.toLowerCase().replace(/\s+/g, '-')}`}
                                className="group"
                            >
                                <div
                                    className="aspect-square w-full transition-transform duration-500 group-hover:scale-105"
                                    style={{ backgroundColor: s.hex }}
                                />
                                <p
                                    className="mt-2 text-[9px] uppercase tracking-[0.2em]"
                                    style={{ color: '#F7EBD5' }}
                                >
                                    {s.name}
                                </p>
                                <p
                                    className="text-[9px] italic"
                                    style={{ color: '#A89470' }}
                                >
                                    {s.meaning}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>

                {/* Right: two-image collage with parallax */}
                <div className="lg:col-span-6 relative h-[560px] md:h-[640px] lg:h-[720px]">
                    <motion.div
                        style={{ y: leftY }}
                        className="absolute left-0 top-0 w-[62%] h-[70%] overflow-hidden shadow-2xl shadow-black/60"
                    >
                        <img
                            src="/portfolio/p6_bride.webp"
                            alt="Telugu bride being adorned"
                            loading="lazy"
                            className="w-full h-full object-cover"
                        />
                    </motion.div>
                    <motion.div
                        style={{ y: rightY }}
                        className="absolute right-0 bottom-0 w-[62%] h-[62%] overflow-hidden shadow-2xl shadow-black/60"
                    >
                        <img
                            src="/portfolio/p5_waterfall.webp"
                            alt="Telugu wedding ritual · the sacred coconut"
                            loading="lazy"
                            className="w-full h-full object-cover"
                        />
                    </motion.div>

                    {/* floating label */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.6 }}
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 text-center pointer-events-none"
                    >
                        <span
                            className="font-serif italic text-6xl md:text-7xl lg:text-8xl tracking-tighter opacity-70"
                            style={{ color: '#F7EBD5' }}
                        >
                            &
                        </span>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
